// FRSCALE1 (docs/build-briefs/FRSCALE1-royalty-at-scale.md; design/notes/franchisor-scale-audit.md): the pure half of the ONE
// shared franchisor list. Every franchisor page that has a row per location or per franchisee (Royalty now, the other pages in
// FRSCALE2) groups, filters, sorts and pages through these functions. No DOM, no fetch.
//
// The shape: a GROUP is a franchisee and its ROWS are its locations. Filtering keeps a group when any of its rows passes (and,
// for a page whose group row is itself the thing shown, keeps the group's whole row list so an editor still sees every
// location). Sorting orders the rows inside each group by the column and orders the groups by the column's own group value
// (a subtotal, or the worst status). Paging is by group, so one franchisee is never split across two pages.

import { compareNames } from "./nameSort.ts";

export interface ListRow {
  /** Unique across the whole list. */
  id: string;
  groupId: string;
  name: string;
  /** Two letter state, or null when unknown (the state filter is then not offered). */
  state: string | null;
  /** FRMERGE1: the muted line after the row name, such as "Toronto, ON". Defaults to the state. */
  detail?: string | null;
}

export interface ListGroup<R extends ListRow, G = unknown> {
  id: string;
  name: string;
  state: string | null;
  rows: R[];
  /** Whatever the page needs to draw the group row (a franchisee's rates, for example). */
  meta: G;
}

export interface SelectOption {
  value: string;
  label: string;
}

/** An extra select filter beyond franchisee and state (Status, Show). It tests a row, or the whole group. */
export interface ListFilterDef<R extends ListRow, G> {
  id: string;
  label: string;
  allLabel: string;
  options: SelectOption[];
  testRow?: (row: R, group: ListGroup<R, G>, value: string) => boolean;
  testGroup?: (group: ListGroup<R, G>, value: string) => boolean;
  /** The status style filter: choosing it opens every group so the matching rows are seen. */
  opensGroups?: boolean;
  /** FRMERGE1: false for a value that filters whole groups only, so a group with no rows survives it. Default true when testRow is set. */
  rowsApply?: (value: string) => boolean;
}

export interface ListSortDef<R extends ListRow, G> {
  id: string;
  /** Value to order the rows inside a group; omit to order by name. */
  row?: (row: R) => number | string;
  /** Value to order the groups; omit to order by group name. */
  group?: (group: ListGroup<R, G>) => number | string;
  /** True for money and counts: the first click sorts largest first. */
  numeric?: boolean;
}

export interface ListQuery {
  search: string;
  franchisee: string;
  state: string;
  filters: Record<string, string>;
  sortId: string;
  sortDir: 1 | -1;
  page: number;
}

export function emptyQuery(sortId: string): ListQuery {
  return { search: "", franchisee: "", state: "", filters: {}, sortId, sortDir: 1, page: 0 };
}

export function anyFilterActive(query: ListQuery): boolean {
  return (
    query.search.trim() !== "" ||
    query.franchisee !== "" ||
    query.state !== "" ||
    Object.values(query.filters).some((v) => v !== "")
  );
}

export interface FilterHooks<R extends ListRow, G> {
  searchText: (row: R, group: ListGroup<R, G>) => string;
  filters: readonly ListFilterDef<R, G>[];
  /** True keeps every row of a surviving group (the group row is the thing shown); false shows only the rows that match. */
  prune: boolean;
}

/** Groups that survive the query, with their rows pruned when asked. Never mutates its input. */
export function filterGroups<R extends ListRow, G>(
  groups: readonly ListGroup<R, G>[],
  query: ListQuery,
  hooks: FilterHooks<R, G>,
): ListGroup<R, G>[] {
  const needle = query.search.trim().toLowerCase();
  const active = hooks.filters
    .map((def) => ({ def, value: query.filters[def.id] ?? "" }))
    .filter((entry) => entry.value !== "");
  const rowFiltersOn =
    needle !== "" ||
    query.state !== "" ||
    active.some((e) => e.def.testRow !== undefined && (e.def.rowsApply?.(e.value) ?? true));
  const out: ListGroup<R, G>[] = [];
  for (const group of groups) {
    if (query.franchisee !== "" && group.id !== query.franchisee) continue;
    if (!active.every((e) => e.def.testGroup === undefined || e.def.testGroup(group, e.value))) continue;
    const kept = group.rows.filter((row) => {
      if (needle !== "" && !hooks.searchText(row, group).toLowerCase().includes(needle)) return false;
      if (query.state !== "" && row.state !== query.state) return false;
      return active.every((e) => e.def.testRow === undefined || e.def.testRow(row, group, e.value));
    });
    if (rowFiltersOn && kept.length === 0) continue;
    out.push({ ...group, rows: hooks.prune ? kept : group.rows });
  }
  return out;
}

function compareValues(a: number | string, b: number | string): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return compareNames(String(a), String(b));
}

/** Rows inside each group by the column, groups by the column's group value; ties fall back to the name. New arrays. */
export function sortGroups<R extends ListRow, G>(
  groups: readonly ListGroup<R, G>[],
  query: Pick<ListQuery, "sortId" | "sortDir">,
  sorts: readonly ListSortDef<R, G>[],
): ListGroup<R, G>[] {
  const def = sorts.find((s) => s.id === query.sortId);
  const dir = query.sortDir;
  const rowValue = (row: R): number | string => def?.row?.(row) ?? row.name;
  const groupValue = (group: ListGroup<R, G>): number | string => def?.group?.(group) ?? group.name;
  const sortedRows = (group: ListGroup<R, G>): R[] =>
    [...group.rows].sort((a, b) => dir * compareValues(rowValue(a), rowValue(b)) || compareNames(a.name, b.name));
  return groups
    .map((group) => ({ ...group, rows: sortedRows(group) }))
    .sort((a, b) => dir * compareValues(groupValue(a), groupValue(b)) || compareNames(a.name, b.name));
}

/** What a click on a header does: the same column flips the direction; a new column starts largest first or A to Z. */
export function nextSort(
  current: Pick<ListQuery, "sortId" | "sortDir">,
  clicked: string,
  numeric: boolean,
): { sortId: string; sortDir: 1 | -1 } {
  if (current.sortId === clicked) return { sortId: clicked, sortDir: current.sortDir === 1 ? -1 : 1 };
  return { sortId: clicked, sortDir: numeric ? -1 : 1 };
}

export interface PageInfo<T> {
  items: T[];
  page: number;
  pages: number;
  /** 1 based first and last group shown, 0 when none. */
  from: number;
  to: number;
  total: number;
}

export function pageOf<T>(items: readonly T[], page: number, size: number): PageInfo<T> {
  const pages = Math.max(1, Math.ceil(items.length / size));
  const clamped = Math.min(Math.max(0, page), pages - 1);
  const start = clamped * size;
  const slice = items.slice(start, start + size);
  return {
    items: slice,
    page: clamped,
    pages,
    from: items.length === 0 ? 0 : start + 1,
    to: Math.min(items.length, start + size),
    total: items.length,
  };
}

export function rowTotal<R extends ListRow, G>(groups: readonly ListGroup<R, G>[]): number {
  return groups.reduce((sum, group) => sum + group.rows.length, 0);
}

/** "Franchisees 1 to 6 of 25 and 116 locations": the pager's plain sentence. */
export function pagerText(info: PageInfo<unknown>, groupNoun: string, rowNoun: string | null, rowsShown: number): string {
  const base = `${groupNoun} ${info.from} to ${info.to} of ${info.total}`;
  return rowNoun === null ? base : `${base} · ${rowsShown} ${rowNoun}`;
}

/** "1 location" or "5 locations". */
export function countText(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

export interface Tile {
  id: string;
  label: string;
  value: string;
  sub: string;
  /** The exact figure, as the tile's tooltip, when `value` is shortened. */
  title?: string;
  /** A problem tile turns red and carries a "!" (set by the page, words stay in the value or sub). */
  bad?: boolean;
}

/** Money in whole dollars for a subtotal ("$254,790"), half up, integer arithmetic only. Absent is blank, never N/A. */
export function wholeDollars(cents: number | null): string {
  if (cents === null) return "";
  const magnitude = Math.abs(cents);
  const dollars = Math.floor((magnitude + 50) / 100);
  const text = String(dollars).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${cents < 0 ? "-" : ""}$${text}`;
}

/** Money to the cent for a row ("$35,910.00"). Absent is blank, never N/A. */
export function exactDollars(cents: number | null): string {
  if (cents === null) return "";
  const magnitude = Math.abs(cents);
  const text = String(Math.floor(magnitude / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${cents < 0 ? "-" : ""}$${text}.${String(magnitude % 100).padStart(2, "0")}`;
}

/** Tile money that always fits: $8,303 stays exact, $184,200 is "$184.2K", $2,119,000 is "$2.12M". Half up, integer arithmetic only. */
export function compactDollars(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const dollars = Math.floor((Math.abs(cents) + 50) / 100);
  const trim = (n: string): string => n.replace(/\.?0+$/, "");
  if (dollars >= 1_000_000) {
    const hundredths = Math.floor((dollars + 5_000) / 10_000);
    return `${sign}$${trim(`${Math.floor(hundredths / 100)}.${String(hundredths % 100).padStart(2, "0")}`)}M`;
  }
  if (dollars >= 10_000) {
    const tenths = Math.floor((dollars + 50) / 100);
    return `${sign}$${trim(`${Math.floor(tenths / 10)}.${tenths % 10}`)}K`;
  }
  return wholeDollars(cents);
}

// HQFRAN1 (B1): one wording for how many locations a royalty figure counts, used by Billing, Locations and the Dashboard.
/** "from 3 of 6 locations", or "from all 6 locations" when every location has a rate. */
export function royaltyCoverageText(rated: number, total: number): string {
  if (total > 0 && rated >= total) return `from all ${total} ${total === 1 ? "location" : "locations"}`;
  return `from ${rated} of ${total} locations`;
}

/** The one sentence that says why a royalty total is short: the locations with no rate are not counted. Empty when all are rated. */
export function royaltyGapSentence(rated: number, total: number): string {
  const missing = total - rated;
  if (missing <= 0 || total <= 0) return "";
  const who = missing === 1 ? "1 location has" : `${missing} locations have`;
  return `${who} no royalty rate set, so ${missing === 1 ? "it is" : "they are"} not counted in these totals.`;
}
